import { useState } from "react";
import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { farmCoverAlt, farmPhotoAlt } from "@/lib/imageAlt";
import { farmReopeningLabel, farmTypes, farmVillages, isFarmOpen, type Farm } from "@/mocks/farms";

interface FarmsListingsProps {
  farms: Farm[];
  selectedSlug: string;
  onSelect: (slug: string) => void;
  /** Opens the "notify me when it reopens" form for a closed farm. */
  onNotify: (farm: Farm) => void;
}

const availabilityFilters = [
  { id: "all", label: "All farms" },
  { id: "open", label: "Taking bookings now" },
  { id: "reopening", label: "Reopening soon" },
] as const;

type AvailabilityFilter = (typeof availabilityFilters)[number]["id"];

function FarmCard({
  farm,
  active,
  onSelect,
  onNotify,
}: {
  farm: Farm;
  active: boolean;
  onSelect: (slug: string) => void;
  onNotify: (farm: Farm) => void;
}) {
  const gallery = [
    { src: farm.image, alt: farmCoverAlt(farm) },
    ...farm.photos.map((src, index) => ({
      src,
      alt: farmPhotoAlt(farm, index, farm.photos.length),
    })),
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const activePhoto = gallery[activeIndex] ?? gallery[0];
  const open = isFarmOpen(farm);
  const reopeningLabel = farmReopeningLabel(farm);

  return (
    <article
      className={`h-full flex flex-col bg-background-50 rounded-lg border overflow-hidden transition-colors ${
        active ? "border-accent-400" : "border-background-200"
      }`}
    >
      <div className="relative w-full h-44 md:h-48 overflow-hidden">
        <img
          src={activePhoto.src}
          width={1200}
          height={900}
          alt={activePhoto.alt}
          title={`${farm.name} - ${farm.crop} farm in ${farm.village}`}
          className="w-full h-full object-cover object-top transition-opacity duration-300"
          decoding="async"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-background-50/90 text-foreground-900 text-xs font-semibold px-3 py-1.5 rounded-full">
          <i className="ri-map-pin-2-line text-accent-600"></i>
          {farm.village}
        </span>
        {!open && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-secondary-900/90 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full">
            <i className="ri-time-line"></i>
            Reopening soon
          </span>
        )}
      </div>

      <div className="grid grid-cols-4 gap-1.5 px-2.5 pt-2.5">
        {gallery.map((photo, index) => (
          <button
            key={`${farm.slug}-photo-${index}`}
            type="button"
            onClick={() => setActiveIndex(index)}
            aria-label={`Show photo ${index + 1} of ${gallery.length}: ${photo.alt}`}
            className={`h-12 md:h-14 rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${
              activeIndex === index ? "border-accent-500" : "border-transparent hover:border-accent-300"
            }`}
          >
            <img
              src={photo.src}
              width={400}
              height={300}
              alt={photo.alt}
              className="w-full h-full object-cover object-top"
              decoding="async"
            />
          </button>
        ))}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold">
          <Link
            to={`/farms/${farm.slug}`}
            className="hover:text-primary-700 transition-colors cursor-pointer"
          >
            {farm.name}
          </Link>
        </h3>
        <p className="text-xs text-foreground-600 mt-1.5">
          Hosted by {farm.farmer} · {farm.area}
        </p>
        <p className="text-sm text-foreground-700 leading-relaxed mt-3">{farm.summary}</p>

        <Link
          to={`/farms/${farm.slug}`}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
        >
          View full farm
          <i className="ri-arrow-right-line"></i>
        </Link>

        <div className="flex flex-wrap gap-2 mt-4">
          {farm.highlights.slice(0, 3).map((item) => (
            <span
              key={item}
              className="text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200 px-2.5 py-1 rounded-full"
            >
              {item}
            </span>
          ))}
        </div>

        <div className="mt-auto pt-5 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => onSelect(farm.slug)}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-secondary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-map-2-line"></i>
              Show on Map
            </button>
            <Link
              to={`/experiences/${farm.experienceSlug}`}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-background-100 text-foreground-900 border border-background-300 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              Experience
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          {open ? (
            <Link
              to={`/experiences/${farm.experienceSlug}#booking-calendar`}
              className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line text-base"></i>
              Reserve Your Date
            </Link>
          ) : (
            <div className="w-full rounded-md border border-secondary-300 bg-secondary-100 px-4 py-3">
              <p className="flex items-start gap-2 text-sm font-medium text-secondary-900 leading-relaxed">
                <i className="ri-time-line text-base shrink-0"></i>
                <span>
                  {reopeningLabel
                    ? `Closed for now — expected back by ${reopeningLabel}.`
                    : "Closed for now — this farm is reopening soon."}{" "}
                  Not taking bookings yet.
                </span>
              </p>
              <button
                type="button"
                onClick={() => onNotify(farm)}
                className="mt-2.5 w-full inline-flex items-center justify-center gap-2 bg-secondary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-notification-3-line text-base"></i>
                Notify me when it reopens
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export default function FarmsListings({
  farms,
  selectedSlug,
  onSelect,
  onNotify,
}: FarmsListingsProps) {
  const [active, setActive] = useState("all");
  const [activeVillage, setActiveVillage] = useState("all");
  const [availability, setAvailability] = useState<AvailabilityFilter>("all");
  const filtered = farms.filter((farm) => {
    const matchesCrop = active === "all" || farm.types.includes(active);
    const matchesVillage = activeVillage === "all" || farm.village === activeVillage;
    const matchesAvailability =
      availability === "all" ||
      (availability === "open" ? isFarmOpen(farm) : !isFarmOpen(farm));
    return matchesCrop && matchesVillage && matchesAvailability;
  });
  const reopeningCount = farms.filter((farm) => !isFarmOpen(farm)).length;
  const filtersActive =
    active !== "all" || activeVillage !== "all" || availability !== "all";

  return (
    <section id="all-farms" className="sun-glaze bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-8 md:mb-10">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              All Farms
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            Browse Farms by Crop & Village
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Filter by what grows there, narrow it down to a village, or tap a farm to pin it on the
            map above — then jump straight into the experience it hosts.
          </p>
        </Reveal>

        <Reveal className="mb-10">
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                By availability
              </p>
              <div className="flex flex-wrap gap-2">
                {availabilityFilters.map((filter) => {
                  const isActive = filter.id === availability;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => setAvailability(filter.id)}
                      aria-pressed={isActive}
                      className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "bg-primary-600 border-primary-600 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {filter.id === "reopening" && <i className="ri-time-line"></i>}
                      {filter.label}
                      {filter.id === "reopening" && reopeningCount > 0 && (
                        <span
                          className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                            isActive
                              ? "bg-background-50/25 text-background-50"
                              : "bg-secondary-100 text-secondary-900"
                          }`}
                        >
                          {reopeningCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                By crop
              </p>
              <div className="flex flex-wrap gap-2">
                {farmTypes.map((type) => {
                  const isActive = type.id === active;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setActive(type.id)}
                      className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "bg-secondary-500 border-secondary-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-700 hover:border-secondary-300 hover:text-secondary-700"
                      }`}
                    >
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-3">
                By village
              </p>
              <div className="flex flex-wrap gap-2">
                {farmVillages.map((village) => {
                  const isActive = village.id === activeVillage;
                  return (
                    <button
                      key={village.id}
                      type="button"
                      onClick={() => setActiveVillage(village.id)}
                      className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "bg-accent-500 border-accent-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-700 hover:border-accent-300 hover:text-accent-700"
                      }`}
                    >
                      {village.id !== "all" && <i className="ri-map-pin-2-line"></i>}
                      {village.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>

        <p className="text-sm text-foreground-600 mb-6">
          Showing {filtered.length} of {farms.length} farms
          {reopeningCount > 0 && (
            <span className="text-foreground-500">
              {" "}
              · {reopeningCount} reopening soon and not yet taking bookings
            </span>
          )}
          {filtersActive && (
            <button
              type="button"
              onClick={() => {
                setActive("all");
                setActiveVillage("all");
                setAvailability("all");
              }}
              className="ml-3 inline-flex items-center gap-1 text-primary-700 font-semibold hover:text-primary-800 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
              Clear filters
            </button>
          )}
        </p>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((farm, index) => (
              <Reveal key={farm.slug} delay={(index % 3) * 80} className="h-full">
                <FarmCard
                  farm={farm}
                  active={farm.slug === selectedSlug}
                  onSelect={onSelect}
                  onNotify={onNotify}
                />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="bg-background-50 rounded-lg border border-background-200 py-16 px-6 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-map-pin-line text-3xl"></i>
            </span>
            <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
              No farms match that combination
            </h3>
            <p className="mt-3 text-foreground-600 leading-relaxed max-w-md mx-auto">
              {availability === "reopening"
                ? "No closed farms match the crop and village you picked — clear the filters to see every farm that is reopening soon."
                : "Try another crop or village — or clear both to see every farm in the delta."}
            </p>
            <button
              type="button"
              onClick={() => {
                setActive("all");
                setActiveVillage("all");
                setAvailability("all");
              }}
              className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
}